import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getDeleteTokenDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/token/delete-token.form-default-values';
import { DeleteTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/token/delete-token.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { TokenDeleteFormController } from '@app/feature/token/token-delete/token-delete-form/token-delete-form.controller';
import {
  DeleteTokenContextProvider,
  useTokenContext,
} from '@app/feature/token/token.context';
import { useCpmsBoDeleteTokenMutation } from '@service/cpms-bo-token.service';

interface TokenDeleteControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const TokenDeleteController = ({
  onCancel,
  onSuccess,
}: TokenDeleteControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [deleteTokenMutation] = useCpmsBoDeleteTokenMutation();
  const token = useTokenContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<Pick<DeleteTokenModel, 'id'>> = (
    errors,
  ) => {
    Logger.error(errors);
    toast.error(t('$.token-delete.error', { error: t('global.unknown') }));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<DeleteTokenModel, 'id'>
  > = (fieldValues) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    deleteTokenMutation({
      id: fieldValues.id,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.token-delete.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(t('$.token-delete.error', { error: errorMessage }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getDeleteTokenDefaultValues(token.id);

  return (
    <DeleteTokenContextProvider data={data}>
      <TokenDeleteFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </DeleteTokenContextProvider>
  );
};
